import React, { useState, useEffect, useCallback } from "react";
import { Button, Text } from "@radix-ui/themes";
import { FaceLivenessDetector } from "@aws-amplify/ui-react-liveness";
import { useFaceLivenessService } from "../useFaceLivenessService";

const Liveness = (props) => {
  const {
    validationID,
    currentStepID,
    attemptId,
    onSuccess,
    onFail,
    onValidationCount,
    onSessionUpdate,
  } = props;

  const {
    createStreamSession,
    getLivenessSessionResult,
    compareProfileWithLivePerson,
  } = useFaceLivenessService();

  const [isStarted, setIsStarted] = useState(false);
  const [faceLivenessSessionId, setFaceLivenessSessionId] = useState(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isStarted) {
      onFaceLivenessStart();
    }

    return () => {
      console.log("cleanup");
      // setFaceLivenessSessionId(null);
      // setIsStarted(false);
    };
  }, [isStarted]);

  const onFaceLivenessStart = async () => {
    setLoading(true);

    try {
      const res = await createStreamSession({
        validationID: validationID,
        currentStepID: currentStepID,
        attemptId: attemptId,
      });
      onSessionUpdate?.(res.session);
      if (Number.isFinite(res.validationCount) && onValidationCount) {
        onValidationCount(res.validationCount);
      }
      if (res.status === "success") {
        setFaceLivenessSessionId(res.data.SessionId);
        setIsStarted(true);
      } else {
        onFail({
          failMessage: res.message || "Failed to start liveness session.",
          validationCount: res.validationCount,
          isRetryLimit:
            res.message === "Exceeded retry limit for this request.",
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      onFail({
        failMessage: "Failed to start liveness session.",
      });
    }
  };

  const CustomError = useCallback(() => {
    return (
      <div className="flex items-center justify-center bg-red-100 text-red-600 border rounded-lg p-4 gap-4">
        <Text>{error?.error.message}</Text>
        <Button onClick={reset}>Try Again</Button>
      </div>
    );
  }, [error]);

  const reset = () => {
    setFaceLivenessSessionId(null);
    onFaceLivenessStart();
  };

  const onAnalysisComplete = async (sessionId) => {
    try {
      const res = await getLivenessSessionResult({
        validationID: validationID,
        sessionId: sessionId,
      });
      if (res.status === "success") {
        onSessionUpdate?.(res.session);
        onCompareFaces();
      } else {
        onFail({
          failMessage: "Failed to verify face liveness. Please try again.",
          validationCount: res.validationCount,
        });
      }
    } catch (error) {
      onFail({
        failMessage: "Failed to verify face liveness. Please try again.",
      });
    }
  };

  const onCompareFaces = async () => {
    const res = await compareProfileWithLivePerson({
      validationID: validationID,
    });
    onSessionUpdate?.(res.session);
    if (res.status === "success") {
      onSuccess(validationID);
    } else {
      onFail({
        failMessage: res.message,
        validationCount: res.validationCount,
      });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen">
      {loading ? (
        <Text>Loading. Please wait.</Text>
      ) : (
        <FaceLivenessDetector
          disableStartScreen={true}
          sessionId={faceLivenessSessionId}
          region="ap-northeast-1"
          onAnalysisComplete={() => onAnalysisComplete(faceLivenessSessionId)}
          onError={(error) => setError(error)}
          onUserCancel={() => {
            setFaceLivenessSessionId();
            setIsStarted(false);
          }}
          components={{
            ErrorView: CustomError,
          }}
        />
      )}
    </div>
  );
};

export default Liveness;
